// src/hooks/useAccounts.js
// Production-hardened accounts hook
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export function useAccounts() {
  const { user } = useAuth();
  const [accounts,        setAccounts]        = useState([]);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [loading,         setLoading]         = useState(true);
  const initializedRef = useRef(false);

  const fetchAccounts = useCallback(async () => {
    if (!user) { setAccounts([]); setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at');

      if (error) throw error;
      const list = data || [];
      setAccounts(list);

      // Set default account on first load only
      if (!initializedRef.current && list.length > 0) {
        const def = list.find(a => a.is_default) || list[0];
        setActiveAccountId(def.id);
        initializedRef.current = true;
      }
      // If active account was deleted, reset
      if (activeAccountId && !list.find(a => a.id === activeAccountId) && list.length > 0) {
        setActiveAccountId(list[0].id);
      }
    } catch (e) {
      console.error('Accounts fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [user, activeAccountId]);

  useEffect(() => { fetchAccounts(); }, [user]); // eslint-disable-line

  const createAccount = async ({ name, type, broker, startingBalance, propTarget, propDailyLimit, propMaxDrawdown }) => {
    if (!user || !name) return { error: { message: 'Account name required.' } };
    try {
      const { data, error } = await supabase
        .from('accounts')
        .insert([{
          user_id:           user.id,
          name:              name.trim(),
          type:              type || 'Live',
          broker:            broker?.trim() || null,
          starting_balance:  Number(startingBalance) || 0,
          prop_target:       Number(propTarget)       || null,
          prop_daily_limit:  Number(propDailyLimit)   || null,
          prop_max_drawdown: Number(propMaxDrawdown)  || null,
        }])
        .select()
        .single();

      if (!error && data) {
        setAccounts(prev => [...prev, data]);
        setActiveAccountId(data.id);
      }
      return { data, error };
    } catch (e) {
      return { error: { message: 'Failed to create account.' } };
    }
  };

  const deleteAccount = async (id) => {
    if (!user || !id) return;
    const { error } = await supabase.from('accounts').delete().eq('id', id).eq('user_id', user.id);
    if (!error) {
      const remaining = accounts.filter(a => a.id !== id);
      setAccounts(remaining);
      if (activeAccountId === id) {
        setActiveAccountId(remaining[0]?.id || null);
      }
    }
    return { error };
  };

  const activeAccount = accounts.find(a => a.id === activeAccountId) || accounts[0] || null;

  return {
    accounts, activeAccount, activeAccountId,
    setActiveAccountId, createAccount, deleteAccount,
    loading, refetch: fetchAccounts
  };
}
